import React, { useEffect, useState } from "react";
import SidebarItems from "./SidebarItems";
import { appLogo } from "../../assets";
import colors from "../../utils/colors";

import { useNavigate } from "react-router-dom";
import { useAppSelector } from "../../hooks/useAppSelector";
import {
  Flex,
  Img,
  Box,
  Text,
  Drawer,
  DrawerOverlay,
  DrawerContent,
  DrawerCloseButton,
  DrawerHeader,
  DrawerBody,
  useBreakpointValue,
  Badge,
} from "@chakra-ui/react";
import PopoverCmp from "../popover/Popover";
import { person } from "../defaultHeader/assets";
import {
  down_Arr,
  SinglePersonIcon,
  SettingsIcon,
  HelpIcon,
  LogoutSmallIcon,
  NotificationIcon,
} from "../../assets";
import {
  TDoctorInfo,
  TClinicAddress,
  TAvailableTimeSlot,
} from "../../apis/types";
import { logoutDoctor } from "../../store/authSlice";
import { useDispatch } from "react-redux";

import { sideBarMenu } from "./utils/SidebarMenu";

import { hasPermission } from "../../utils/permissions";

const getSafeProfilePicUrl = (
  profilePictureData: TDoctorInfo["profilePicture"]
): string | undefined => {
  if (
    profilePictureData === null ||
    typeof profilePictureData === "undefined"
  ) {
    return undefined;
  }
  if (typeof profilePictureData === "string") {
    return profilePictureData;
  }
  if (
    typeof profilePictureData === "object" &&
    Object.prototype.hasOwnProperty.call(profilePictureData, "url") &&
    typeof (profilePictureData as { url?: string }).url === "string"
  ) {
    return (profilePictureData as { url: string }).url;
  }
  return undefined;
};

const calculateIncompleteProfileItems = (
  doctorInfo: TDoctorInfo | null | undefined
): number => {
  if (!doctorInfo) return 10;
  const maxPoints = 10;
  let completedItemsForBadge = 0;

  if (doctorInfo.name && doctorInfo.name.trim() !== "")
    completedItemsForBadge++;
  if (doctorInfo.title && doctorInfo.title.trim() !== "")
    completedItemsForBadge++;
  if (doctorInfo.phoneNumber && doctorInfo.phoneNumber.trim() !== "")
    completedItemsForBadge++;

  let picForCompletenessCheck = person;
  if (
    doctorInfo.profilePicture &&
    typeof doctorInfo.profilePicture === "string"
  ) {
    picForCompletenessCheck = doctorInfo.profilePicture;
  } else {
    const picFromUrlObject = getSafeProfilePicUrl(doctorInfo.profilePicture);
    if (picFromUrlObject) {
      picForCompletenessCheck = picFromUrlObject;
    }
  }
  if (picForCompletenessCheck !== person) completedItemsForBadge++;

  if (doctorInfo.licenseNumber && doctorInfo.licenseNumber.trim() !== "")
    completedItemsForBadge++;
  if (
    doctorInfo.specialty &&
    doctorInfo.specialty.length > 0 &&
    doctorInfo.specialty.some((s) => s && s.trim() !== "")
  )
    completedItemsForBadge++;
  if (doctorInfo.licenseDocuments) {
    completedItemsForBadge++;
  }
  const hasCompleteClinicAddress = doctorInfo.clinics?.some(
    (addr: TClinicAddress) =>
      addr.name && addr.address && addr.city && addr.state
  );
  if (hasCompleteClinicAddress) completedItemsForBadge++;
  const hasCompleteAvailability = doctorInfo.availability?.some(
    (slot: TAvailableTimeSlot) => slot.day && slot.startTime && slot.endTime
  );
  if (hasCompleteAvailability) completedItemsForBadge++;
  if (doctorInfo.email && doctorInfo.email.trim() !== "")
    completedItemsForBadge++;
  return maxPoints - completedItemsForBadge;
};

export interface SidebarProps {
  isDrawerOpen: boolean;
  onDrawerClose: () => void;
  drawerBtnRef?: React.RefObject<HTMLButtonElement>;
}

const Sidebar: React.FC<SidebarProps> = ({
  isDrawerOpen,
  onDrawerClose,
  drawerBtnRef,
}) => {
  const dispatch = useDispatch();

  const handleLogout = (navigate: Function) => {
    dispatch(logoutDoctor());
    sessionStorage.removeItem("created-doctor");
    // console.log("Logout action triggered");
    navigate("/login");
  };
  const navigate = useNavigate();
  const { doctorInfo } = useAppSelector((state) => state.auth);

  // ✅ NEW: get the JWT user (usually contains permissions)
  const authUser = useAppSelector((s) => (s as any).auth?.user);
  const permsSource = authUser ?? doctorInfo;

  const name = doctorInfo?.name || "Doctor";
  const [incompleteItemsCount, setIncompleteItemsCount] = useState(0);
  const isMobile = useBreakpointValue({ base: true, lg: false });

  useEffect(() => {
    setIncompleteItemsCount(calculateIncompleteProfileItems(doctorInfo));
  }, [doctorInfo]);

  let determinedProfilePic = person;
  if (
    doctorInfo?.profilePicture &&
    typeof doctorInfo.profilePicture === "string" &&
    doctorInfo.profilePicture.trim() !== ""
  ) {
    determinedProfilePic = doctorInfo.profilePicture;
  } else {
    const picFromUrlObject = getSafeProfilePicUrl(doctorInfo?.profilePicture);
    if (picFromUrlObject && picFromUrlObject.trim() !== "") {
      determinedProfilePic = picFromUrlObject;
    }
  }
  const currentProfilePic = determinedProfilePic;

  const options = [
    {
      title: "My Profile",
      image: SinglePersonIcon,
      action: () => {
        navigate("/profile");
        if (isMobile) onDrawerClose();
      },
      badgeCount: incompleteItemsCount > 0 ? incompleteItemsCount : undefined,
    },
    {
      title: "Notifications",
      image: NotificationIcon,
      action: () => {
        navigate("/notifications");
        if (isMobile) onDrawerClose();
      },
    },

    {
      title: "Help",
      image: HelpIcon,
      action: () => {
        navigate("/help");
        if (isMobile) onDrawerClose();
      },
    },
    {
      title: "Logout",
      image: LogoutSmallIcon,
      action: () => {
        handleLogout(navigate);
        if (isMobile) onDrawerClose();
      },
    },
  ];

  const UserProfileAndOptionsPopover = () => (
    <Box px="10px" py="20px">
      <PopoverCmp
        popoverTrigger={
          <Flex
            alignItems="center"
            justifyContent="space-between"
            cursor="pointer"
            p="10px"
            borderRadius="md"
            _hover={{ bg: colors.lightGrey }}
            width="100%"
            position="relative"
          >
            <Flex alignItems="center" gap="10px">
              <Box position="relative">
                <Img
                  src={currentProfilePic}
                  boxSize="35px"
                  borderRadius="full"
                  objectFit="cover"
                  alt="profile"
                  onError={(e) => {
                    (e.target as HTMLImageElement).src = person;
                  }}
                />
                {incompleteItemsCount > 0 && (
                  <Badge
                    colorScheme="red"
                    variant="solid"
                    borderRadius="full"
                    position="absolute"
                    top="-1px"
                    right="-1px"
                    fontSize="0.6em"
                    p="3px 5px"
                  >
                    {incompleteItemsCount}
                  </Badge>
                )}
              </Box>

              <Text
                color={colors.dark}
                fontWeight="medium"
                fontSize="20px"
                noOfLines={1}
                flexShrink={0}
              >
                {doctorInfo?.title
                  ? `${doctorInfo.title} ${
                      doctorInfo?.name?.split(" ")[0] ?? ""
                    }`.trim()
                  : doctorInfo?.name?.split(" ")[0] ||
                    doctorInfo?.name ||
                    "User"}
              </Text>
            </Flex>
            <Img src={down_Arr} className="h-[8px]" alt="options" />
          </Flex>
        }
      >
        <Flex gap={"8px"} direction={"column"} minW="200px">
          <Flex
            p={"12px 16px"}
            gap={"12px"}
            alignItems="center"
            borderBottomWidth="1px"
            borderColor="gray.200"
          >
            <Img
              src={currentProfilePic}
              boxSize="35px"
              borderRadius="full"
              objectFit="cover"
              onError={(e) => {
                (e.target as HTMLImageElement).src = person;
              }}
            />
            <Box>
              <Text
                fontFamily={"lato"}
                fontWeight={600}
                fontSize={"16px"}
                color={colors.dark}
                noOfLines={1}
              >
                {name}
              </Text>
              <Text
                fontFamily={"lato"}
                fontWeight={400}
                fontSize={"14px"}
                color={colors.dark}
              >
                {doctorInfo?.roles?.[0]
                  ? doctorInfo.roles[0].charAt(0).toUpperCase() +
                    doctorInfo.roles[0].slice(1)
                  : "User"}
              </Text>
            </Box>
          </Flex>
          {options.map((item) => (
            <Flex
              key={item.title}
              onClick={item.action}
              alignItems={"center"}
              justifyContent="space-between"
              gap={"12px"}
              p={"10px 16px"}
              cursor={"pointer"}
              _hover={{ bg: colors.lightGrey }}
              borderRadius="md"
            >
              <Flex alignItems="center" gap="12px">
                <Img src={item.image} boxSize="18px" alt={item.title} />
                <Text
                  fontFamily={"lato"}
                  fontWeight={400}
                  fontSize={"15px"}
                  color={colors.dark}
                >
                  {item.title}
                </Text>
              </Flex>
              {item.badgeCount && item.badgeCount > 0 && (
                <Badge
                  colorScheme="red"
                  variant="solid"
                  borderRadius="full"
                  fontSize="0.7em"
                  px="2"
                >
                  {item.badgeCount}
                </Badge>
              )}
            </Flex>
          ))}
        </Flex>
      </PopoverCmp>
    </Box>
  );

  // ✅ NEW: filter menu by permission
  const filteredMenu = sideBarMenu.filter(
    (item: any) =>
      !item.requiredPermission ||
      hasPermission(permsSource, item.requiredPermission)
  );

  if (isMobile) {
    return (
      <Drawer
        isOpen={isDrawerOpen}
        placement="left"
        onClose={onDrawerClose}
        finalFocusRef={drawerBtnRef}
      >
        <DrawerOverlay />
        <DrawerContent>
          <DrawerCloseButton />
          <DrawerHeader borderBottomWidth="1px">
            <Img src={appLogo} alt="logo" maxH="40px" />
          </DrawerHeader>
          <DrawerBody
            p={0}
            display="flex"
            flexDirection="column"
            justifyContent="space-between"
          >
            <Box flexGrow={1} overflowY="auto">
              <SidebarItems
                items={filteredMenu}
                onDrawerClose={onDrawerClose}
                isMobile={isMobile}
              />
            </Box>
            <Box flexShrink={0}>
              <UserProfileAndOptionsPopover />
            </Box>
          </DrawerBody>
        </DrawerContent>
      </Drawer>
    );
  }

  return (
    <div
      className={`w-[20%] min-w-[250px] flex flex-col border-r-[1px] h-[100vh]`}
      style={{ borderColor: colors.grey }}
    >
      <div className="w-full flex items-center justify-center py-[40px]">
        <Img src={appLogo} alt="logo" />
      </div>
      <div className="flex flex-1 flex-col justify-between mb-2">
        <SidebarItems
          items={filteredMenu}
          onDrawerClose={onDrawerClose}
          isMobile={isMobile}
        />
        <UserProfileAndOptionsPopover />
      </div>
    </div>
  );
};

export default Sidebar;
